<template>
  <div class="sh-editor">
    <div class="sh-editor__head">
      <!-- No heading: the tab above already says which half this is, and repeating it immediately
           below reads as a mistake. The status line is what belongs here. -->
      <div>
        <small class="text-muted">
          {{ statusText }}
          <a v-if="publishedVersion && storefrontUrl" :href="storefrontUrl" target="_blank"
             rel="noopener noreferrer" class="ms-1">Se den publiserte nettsiden ↗</a>
        </small>
      </div>
      <div class="sh-editor__actions">
        <!--
          The DRAFT, full size. «Se live nettsiden» was the wrong promise: what a merchant is looking at
          in the builder is by definition not what is published, so that button showed them a different
          page and called it theirs. The published page is still one click away — from the status line
          to the left, where saying «Publisert, versjon N» and linking to it belongs.

          The schema travels by postMessage, exactly as it does into the frame. The draft still has no
          address of its own.
        -->
        <button type="button" class="bo_btn_secondary" :disabled="busy || !schema" @click="openPreview">
          <font-awesome-icon :icon="['fas', 'external-link-alt']" /> Forhåndsvis
        </button>
        <span v-if="previewBlocked" class="sh-editor__blocked">
          Nettleseren blokkerte vinduet — tillat sprettoppvinduer for denne siden.
        </span>
        <span v-if="imageError" class="sh-editor__blocked">{{ imageError }}</span>

        <button ref="versionsButton" type="button" class="bo_btn_secondary" :disabled="busy"
                :aria-expanded="showVersions ? 'true' : 'false'" @click="showVersions = !showVersions">
          <font-awesome-icon :icon="['fas', 'history']" /> Tidligere versjoner
        </button>
        <button type="button" class="bo_btn_secondary" :disabled="busy || !publishedVersion" @click="confirmDiscard">
          <font-awesome-icon :icon="['fas', 'undo']" /> Forkast endringer
        </button>
        <button type="button" class="bo_btn_success" :disabled="busy || !schema" @click="publish">
          <font-awesome-icon :icon="['fas', 'globe']" /> {{ publishing ? 'Publiserer…' : 'Publiser' }}
        </button>

        <!--
          An overlay, hung off the button that opens it. In the flow it pushed the whole editor down
          by its own height — and the history holds twenty entries, so the more a merchant had used
          the builder the further the thing they were working on jumped away from them.
        -->
    <div v-if="showVersions" ref="versions" class="sh-versions">
          <h6 class="sh-versions__title">Tidligere versjoner</h6>
          <p class="sh-versions__hint">
            Å hente tilbake en versjon legger den i utkastet ditt. Den live nettsiden endres ikke før du
            publiserer.
          </p>
          <p v-if="!versions.length" class="sh-versions__hint mb-0">Ingen publiserte versjoner ennå.</p>
          <ul v-else class="sh-versions__list">
            <li v-for="v in versions" :key="v.version">
              <span class="sh-versions__no">Versjon {{ v.version }}</span>
              <span class="sh-versions__when">{{ formatTime(v.modifiedTime) }}</span>
              <button type="button" class="bo_btn_secondary" :disabled="busy" @click="restore(v.version)">
                Hent tilbake
              </button>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <loading-spinner :loading="loading"></loading-spinner>

    <div v-if="!loading && !storefrontUrl" class="alert alert-warning">
      Fant ikke adressen til bestillingssiden din, så forhåndsvisningen kan ikke lastes.
      Ta kontakt med support.
    </div>

    <div v-else-if="!loading" class="sh-editor__body">
      <!-- ── seksjonsliste ─────────────────────────────────────────────────────────── -->
      <aside class="sh-editor__rail">
        <!--
          The merchant's own choice: a homepage, or only the ordering site. Off sends their address straight to
          the menu and keeps the page, so the rest of this editor still works and switching back changes nothing.
        -->
        <h6 class="sh-rail__title">Hjemmeside</h6>
        <div class="form-switch-wrapper sh-rail__switch">
          <label class="form-switch">
            <div class="form-switch-container">
              <input type="checkbox" :checked="homeEnabled" :disabled="busy || switchingHome || !publishedVersion"
                     @change="toggleHome($event)" />
              <i></i>
              <span>Vis hjemmesiden på nettstedet ditt</span>
            </div>
          </label>
        </div>
        <p class="sh-rail__hint">
          <template v-if="!publishedVersion">Publiser hjemmesiden først.</template>
          <template v-else-if="homeEnabled">Gjestene møter hjemmesiden først.</template>
          <template v-else>
            Av: nettstedet åpner rett på bestillingssiden. Hjemmesiden er tatt vare på, og du kan fortsatt
            redigere den.
          </template>
        </p>

        <h6 class="sh-rail__title mt-3">Oppsett</h6>
        <p class="sh-rail__hint">Hvilke seksjoner nettsiden har.</p>
        <div class="sh-rail__layouts">
          <button
              v-for="l in layouts"
              :key="l.key"
              type="button"
              class="sh-layout"
              :class="{ 'sh-layout--active': l.key === activeLayoutKey, 'sh-layout--fits': l.recommended }"
              :disabled="busy"
              @click="applyLayout(l)">
            <span class="sh-layout__name">
              {{ l.name }}
              <span v-if="l.key === activeLayoutKey" class="sh-layout__tag sh-layout__tag--active">i bruk</span>
              <span v-else-if="l.recommended" class="sh-layout__tag">passer deg</span>
            </span>
            <span class="sh-layout__desc">{{ l.description }}</span>
          </button>
        </div>

        <h6 class="sh-rail__title mt-3">Utseende</h6>
        <p class="sh-rail__hint">
          Bytt uttrykk når du vil — teksten og bildene dine følger med.
        </p>
        <div class="sh-rail__templates">
          <button
              v-for="t in templates"
              :key="t.key"
              type="button"
              class="sh-tpl"
              :class="{ 'sh-tpl--active': t.signature === (schema && schema.signature) }"
              :title="t.description"
              :disabled="busy"
              @click="applyTemplate(t)">
            <span class="sh-tpl__swatch" :style="swatch(t)"></span>
            <span class="sh-tpl__name">{{ t.name }}</span>
          </button>
        </div>

        <h6 class="sh-rail__title mt-3">Seksjoner</h6>
        <p class="sh-rail__hint">Klikk en seksjon i listen eller på nettsiden for å redigere den.</p>

        <ul class="sh-rail__list">
          <li
              v-for="block in blocks"
              :key="block.id"
              class="sh-rail__item"
              :class="{ 'sh-rail__item--active': block.id === selectedId }"
              @click="selectBlock(block.id)">
            <span class="sh-rail__name">{{ label(block.type) }}</span>
            <span class="sh-rail__tools" @click.stop>
              <button type="button" title="Flytt opp" :disabled="!canMove(block, -1)" @click="move(block.id, -1)">
                <font-awesome-icon :icon="['fas', 'arrow-up']" />
              </button>
              <button type="button" title="Flytt ned" :disabled="!canMove(block, 1)" @click="move(block.id, 1)">
                <font-awesome-icon :icon="['fas', 'arrow-down']" />
              </button>
              <button
                  type="button"
                  class="sh-rail__remove"
                  title="Fjern"
                  :disabled="required(block.type)"
                  @click="confirmRemove(block)">
                <font-awesome-icon :icon="['fas', 'times']" />
              </button>
            </span>
          </li>
        </ul>

        <!--
          «Det vi tilbyr» is the one section with a structure of its own: which points, which of them are
          services, which icon. The words are written on the page, like everywhere else; what a sentence
          cannot say is chosen here.
        -->
        <div v-if="selectedUsp" class="sh-usp-panel">
          <h6 class="sh-rail__title">Det vi tilbyr</h6>
          <p class="sh-rail__hint">
            En tjeneste vises bare når den er slått på, så nettsiden aldri lover noe du ikke tilbyr.
            Teksten skriver du rett på nettsiden.
          </p>
          <ul class="sh-usp-panel__list">
            <li v-for="(item, i) in selectedUsp.items" :key="i" class="sh-usp-panel__item">
              <button type="button" class="sh-usp-panel__remove" title="Fjern punktet" @click="removeUspItem(i)">
                <font-awesome-icon :icon="['fas', 'times']" />
              </button>
              <div class="sh-usp-panel__fields">
                <span class="sh-usp-panel__title">{{ item.title || 'Uten tittel' }}</span>
                <select class="form-select form-select-sm" :value="item.service || ''"
                        @change="setUspService(i, $event.target.value)">
                  <option value="">Eget løfte</option>
                  <option v-for="s in uspServices" :key="s.key" :value="s.key"
                          :disabled="s.key !== item.service && usedServices.indexOf(s.key) >= 0">
                    Tjeneste: {{ s.label }}
                  </option>
                </select>
                <select class="form-select form-select-sm" :value="item.icon"
                        @change="setUspIcon(i, $event.target.value)">
                  <option v-for="icon in uspIcons" :key="icon.key" :value="icon.key">Ikon: {{ icon.label }}</option>
                </select>
                <small v-if="item.service && !serviceOn(item.service)" class="sh-usp-panel__off">
                  Vises ikke på nettsiden før {{ uspNeeds(item.service) }} er slått på.
                </small>
              </div>
              <span class="sh-rail__tools">
                <button type="button" title="Flytt opp" :disabled="i === 0" @click="moveUspItem(i, -1)">
                  <font-awesome-icon :icon="['fas', 'arrow-up']" />
                </button>
                <button type="button" title="Flytt ned" :disabled="i === selectedUsp.items.length - 1"
                        @click="moveUspItem(i, 1)">
                  <font-awesome-icon :icon="['fas', 'arrow-down']" />
                </button>
              </span>
            </li>
          </ul>
          <div v-if="selectedUsp.items.length < maxUspItems" class="sh-usp-panel__add">
            <select class="form-select form-select-sm" v-model="uspToAdd">
              <option value="">Eget løfte</option>
              <option v-for="s in addableUspServices" :key="s.key" :value="s.key">Tjeneste: {{ s.label }}</option>
            </select>
            <button type="button" class="bo_btn mt-2 w-100" @click="addUspItem">
              <font-awesome-icon :icon="['fas', 'plus']" /> Legg til punkt
            </button>
          </div>
          <small v-else class="text-muted d-block">Seks punkter er det meste seksjonen viser.</small>
        </div>

        <div class="sh-rail__add">
          <label class="form-label mb-1">Legg til seksjon</label>
          <select class="form-select form-select-sm" v-model="typeToAdd">
            <option value="">Velg …</option>
            <option v-for="entry in addable" :key="entry.type" :value="entry.type">{{ entry.label }}</option>
          </select>
          <small v-if="hintForType" class="text-muted d-block mt-1">{{ hintForType }}</small>
          <button type="button" class="bo_btn mt-2 w-100" :disabled="!typeToAdd" @click="addBlock">
            <font-awesome-icon :icon="['fas', 'plus']" /> Legg til
          </button>
        </div>

        <!-- Nothing about the SEO text is visible on the page, so it cannot be edited on it. -->
        <div class="sh-rail__seo">
          <h6 class="sh-rail__title">Google</h6>
          <label class="form-label mb-1">Sidetittel</label>
          <input class="form-control form-control-sm" type="text" maxlength="60"
                 :value="seo.title" @input="setSeo('title', $event.target.value)"
                 :placeholder="'Tom = ' + defaultTitle">
          <label class="form-label mb-1 mt-2">Beskrivelse</label>
          <textarea class="form-control form-control-sm" rows="3" maxlength="160"
                    :value="seo.description" @input="setSeo('description', $event.target.value)"
                    placeholder="Én setning om restauranten"></textarea>
        </div>
      </aside>

      <!-- ── den ekte siden ────────────────────────────────────────────────────────── -->
      <section class="sh-editor__stage">
        <div class="sh-stage__bar">
          <div class="sh-stage__device">
            <button type="button" :class="{ active: device === 'desktop' }" @click="device = 'desktop'">
              <font-awesome-icon :icon="['fas', 'desktop']" /> Desktop
            </button>
            <button type="button" :class="{ active: device === 'mobile' }" @click="device = 'mobile'">
              <font-awesome-icon :icon="['fas', 'mobile-alt']" /> Mobil
            </button>
          </div>
          <span class="sh-stage__saved">{{ savedText }}</span>
        </div>

        <div class="sh-stage__frame" :class="'sh-stage__frame--' + device">
          <iframe
              ref="frame"
              :src="editUrl"
              title="Forhåndsvisning av hjemmesiden"
></iframe>
        </div>
      </section>
    </div>

    <!-- The file picker lives here, not in the iframe: Backoffice holds the merchant's session, so it
         is the only side that can upload. The frame asks; this answers. -->
    <input ref="file" type="file" accept="image/*" class="d-none" @change="onFilePicked">

    <confirm-dialogue ref="confirmDialogue" />
  </div>
</template>

<script>
/**
 * The merchant builds their own homepage here.
 *
 * <b>The renderer is the editor.</b> The frame on the right is the REAL Storefront page in edit mode,
 * driven over `postMessage`. Backoffice draws no preview of its own — which is why this file is a few
 * hundred lines instead of a second copy of eleven block components that would drift from the ones
 * that actually serve guests. Protocol: CagistaServices/docs/merchant-homepage-builder.md.
 *
 * <b>Backoffice owns the schema; the frame owns nothing.</b> Every edit arrives as a message, is
 * applied here, and the whole schema is posted back — so there is exactly one copy of the truth, and
 * the frame cannot save, read a draft or reach the backend at all.
 */
import SiteHomeService from "@/services/site/SiteHomeService";
import MediaFileService from "@/services/media/MediaFileService";
import ImageCompressor from "@/util/ImageCompressor";
import SiteImageSlots from "@/util/SiteImageSlots";
import LoadingSpinner from "@/components/other/LoadingSpinner";
import ConfirmDialogue from "@/components/other/ConfirmDialogue.vue";
import {
  BLOCK_CATALOGUE, addableTypes, blockLabel, insertIndex, isRequired, makeBlock, moveBlock, setPath,
  USP_SERVICES, USP_ICONS, MAX_USP_ITEMS, newUspItem
} from "@/util/SiteHomeBlocks";

const MSG = 'cagista-home';

/** Long enough that typing does not write on every keystroke, short enough to feel automatic. */
const AUTOSAVE_MS = 900;

export default {
  name: "SiteHome",
  emits: ["published"],
  components: { LoadingSpinner, ConfirmDialogue },
  data() {
    return {
      loading: true,
      saving: false,
      // Why an upload was refused, or could not finish. Stays until the merchant picks again.
      imageError: "",
      publishing: false,
      schema: null,
      publishedVersion: 0,
      storefrontUrl: "",
      selectedId: "",
      device: "desktop",
      typeToAdd: "",
      versions: [],
      templates: [],
      layouts: [],
      showVersions: false,
      savedAt: null,
      /** Set while applying an incoming patch, so the autosave watcher does not echo it straight back. */
      saveTimer: null,
      /** What the frame asked us to fill, from an `image` message. */
      pendingImage: null,
      previewBlocked: false,
      storefrontBase: '',
      /** Which services the merchant offers right now, from CagistaServices — the rule the page itself uses. */
      services: {},
      /** Whether their address shows the homepage, or goes straight to the ordering site. */
      homeEnabled: true,
      switchingHome: false,
      uspToAdd: "",
      uspServices: USP_SERVICES,
      uspIcons: USP_ICONS,
      maxUspItems: MAX_USP_ITEMS
    };
  },
  computed: {
    busy() {
      return this.loading || this.saving || this.publishing;
    },
    blocks() {
      return (this.schema && this.schema.blocks) || [];
    },
    seo() {
      return (this.schema && this.schema.seo) || {};
    },
    /**
     * Which layout the page is on right now, by comparing its section list to each layout's.
     *
     * <b>Not the same question as which one fits.</b> «passer dere» is about how many venues the
     * merchant has; this is about what they have actually chosen. Showing only the recommendation
     * made the suggested card look selected, which is a confident-looking lie when they are on the
     * other one. A merchant who has since added or removed a section matches neither, and then
     * nothing is marked — which is honest.
     */
    activeLayoutKey() {
      const current = this.blocks.map(b => b.type).join(' ');
      const match = this.layouts.find(l => (l.blocks || []).join(' ') === current);
      return match ? match.key : '';
    },

    addable() {
      return addableTypes(this.blocks);
    },
    /** The «Det vi tilbyr» section, when that is the one selected — the only section with its own panel. */
    selectedUsp() {
      const block = this.blocks.find(b => b.id === this.selectedId);
      return block && block.type === "usp" ? block : null;
    },
    /** A service once per section: two «Levering» points would say the same thing twice. */
    usedServices() {
      return ((this.selectedUsp && this.selectedUsp.items) || []).map(i => i.service).filter(s => !!s);
    },
    addableUspServices() {
      return USP_SERVICES.filter(s => this.usedServices.indexOf(s.key) < 0);
    },
    hintForType() {
      const entry = BLOCK_CATALOGUE.find(b => b.type === this.typeToAdd);
      return entry ? entry.hint : "";
    },
    editUrl() {
      return this.storefrontUrl ? this.storefrontUrl + "/?edit=1" : "";
    },
    frameOrigin() {
      try {
        return new URL(this.storefrontUrl).origin;
      } catch (e) {
        return "";
      }
    },
    defaultTitle() {
      return "restaurantens navn + «Bestill mat på nett»";
    },
    statusText() {
      if (this.loading) return "Laster …";
      if (!this.publishedVersion) return "Ikke publisert ennå — gjestene ser den ikke før du trykker Publiser.";
      return "Publisert, versjon " + this.publishedVersion + ". Endringer du gjør nå er et utkast.";
    },
    savedText() {
      if (this.saving) return "Lagrer …";
      return this.savedAt ? "Utkast lagret " + this.savedAt : "";
    }
  },
  /**
   * The preview window is held HERE, not in data().
   *
   * Vue makes everything in `data()` deeply reactive, and a reactive proxy around a CROSS-ORIGIN
   * Window throws `SecurityError` on the first property read — the proxy probes `__v_isRef`, which the
   * browser refuses across origins. So `previewWindow.closed` threw, every post was swallowed, and
   * `event.source === this.previewWindow` compared a raw Window against a proxy and was false. The
   * preview opened and then quietly showed the published page.
   *
   * `markRaw()` is the usual escape and does not work either: it WRITES a marker onto the object,
   * which is refused across origins for the same reason. An instance property assigned outside
   * `data()` is never made reactive, which is what this needs — nothing renders from it.
   */
  created() {
    this.previewWindow = null;
  },
  mounted() {
    window.addEventListener("message", this.onMessage);
    document.addEventListener("click", this.onDocumentClick, true);
    document.addEventListener("keydown", this.onKeydown);
    this.load();
  },
  beforeUnmount() {
    window.removeEventListener("message", this.onMessage);
    document.removeEventListener("click", this.onDocumentClick, true);
    document.removeEventListener("keydown", this.onKeydown);
    if (this.saveTimer) clearTimeout(this.saveTimer);
  },
  methods: {
    label: blockLabel,
    required: isRequired,

    load() {
      this.loading = true;
      SiteHomeService.getHome().then(
          (response) => {
            const data = response.data || {};
            this.schema = data.schema || null;
            this.services = data.services || {};
            this.homeEnabled = data.homeEnabled !== false;
            this.publishedVersion = data.publishedVersion || 0;
            this.storefrontUrl = data.storefrontUrl || "";
            this.storefrontBase = data.storefrontUrl || "";
            this.loading = false;
            this.postSchema();
            this.loadVersions();
            this.loadTemplates();
            this.loadLayouts();
          },
          () => { this.loading = false; }
      );
    },

    loadLayouts() {
      SiteHomeService.getLayouts().then((response) => { this.layouts = response.data || []; });
    },

    /**
     * Structure, not appearance — the two are separate choices, and mixing them is how a merchant
     * who wanted a warmer palette ended up with sections they never asked for.
     */
    async applyLayout(l) {
      const ok = await this.$refs.confirmDialogue.show({
        title: 'Bytt til «' + l.name + '»',
        message: l.description + ' Teksten og bildene i seksjonene du beholder følger med. '
            + 'Den live nettsiden endres ikke før du publiserer.',
        okButton: 'Bytt oppsett',
        cancelButton: 'Avbryt'
      });
      if (!ok) return;
      SiteHomeService.applyLayout(l.key, this.schema).then((response) => {
        if (response.data && response.data.schema) {
          this.schema = response.data.schema;
          this.postSchema();
          this.queueSave();
        }
      });
    },

    loadTemplates() {
      SiteHomeService.getTemplates().then((response) => { this.templates = response.data || []; });
    },

    /** A three-colour chip is enough to choose by, and it is the template's real palette. */
    swatch(t) {
      const theme = t.theme || {};
      return {
        background: 'linear-gradient(135deg, ' + theme.colorBackground + ' 0 46%, '
            + theme.colorPrimary + ' 46% 72%, ' + theme.colorSurface + ' 72% 100%)'
      };
    },

    /**
     * No confirmation. A look is meant to be TRIED — five dialogs to compare five palettes is friction
     * on the one interaction the picker exists for, and nothing is at stake: it touches no text, no
     * photograph and no section, the live page is untouched until Publiser, and the next click undoes
     * it. The layout picker still asks, because that one adds and removes sections.
     */
    applyTemplate(t) {
      SiteHomeService.applyTemplate(t.key, this.schema).then((response) => {
        if (response.data && response.data.schema) {
          this.schema = response.data.schema;
          this.postSchema();
          this.queueSave();
        }
      });
    },

    /** An overlay has to close the way every other overlay does, or it is a panel that gets stuck. */
    onDocumentClick(event) {
      if (!this.showVersions) return;
      const panel = this.$refs.versions;
      const button = this.$refs.versionsButton;
      if (panel && panel.contains(event.target)) return;
      if (button && button.contains(event.target)) return;
      this.showVersions = false;
    },

    onKeydown(event) {
      if (event.key === "Escape" && this.showVersions) this.showVersions = false;
    },

    loadVersions() {
      SiteHomeService.getVersions().then((response) => { this.versions = response.data || []; });
    },

    /**
     * Open the draft in its own window and post the schema in.
     *
     * <b>Held until the window says it is listening.</b> A preview opened and written to in the same
     * breath is a race the window loses about half the time, and the merchant sees the published page
     * instead of their draft with nothing to explain the difference. The window announces itself the
     * same way the frame does.
     */
    openPreview() {
      if (!this.storefrontBase) return;
      const win = window.open(this.storefrontBase + "/?preview=1", "cagista-home-preview");
      if (!win) {
        // A blocked popup is indistinguishable from a broken button: the merchant clicks and nothing
        // happens, with no way to know a browser setting did it rather than us.
        this.previewBlocked = true;
        return;
      }
      this.previewBlocked = false;
      this.previewWindow = win;
    },

    /* ── the frame ──────────────────────────────────────────────────────────────── */

    /**
     * Send to the frame.
     *
     * <b>Not gated on a handshake flag, deliberately.</b> It used to be, and that flag was a single
     * point of permanent failure: anything that reset it without ALSO reloading the iframe — a
     * hot-reload in development, any future remount of the editor alone — left the frame alive,
     * listening, and never written to again. The merchant clicks «Varm kafé», the picker highlights
     * it, and the page beside it does not move. Nothing is logged, and the only way back is a full
     * browser refresh. Proved by dropping the flag by hand: every subsequent template click changed
     * nothing.
     *
     * Posting unconditionally cannot go wrong in the other direction. A message that arrives before
     * the frame has mounted is dropped by the browser, and the frame asks for the schema itself the
     * moment it IS mounted — that is what its `ready` is for. So the handshake still does its job;
     * it just no longer has a veto over everything that comes after it.
     */
    postToFrame(message) {
      const frame = this.$refs.frame;
      if (!this.frameOrigin || !frame || !frame.contentWindow) return;
      frame.contentWindow.postMessage(message, this.frameOrigin);
    },

    postSchema() {
      if (!this.frameOrigin) return;
      const payload = { source: MSG, type: "schema", schema: JSON.parse(JSON.stringify(this.schema)) };
      this.postToFrame(payload);
      this.postToPreview(payload);
    },

    /**
     * Tell the frame what size each image slot wants, so its upload placeholders can say so before
     * the merchant opens a file picker there is nothing to read in.
     *
     * Sent rather than duplicated in the storefront: this side owns the table, because this side is
     * the one that refuses an image for being too small. A second copy over there would be the copy
     * that drifted, advising one size while this end demanded another.
     */
    postSlots() {
      const slots = {};
      Object.keys(SiteImageSlots.SLOTS).forEach((key) => {
        const s = SiteImageSlots.SLOTS[key];
        const min = s.min || { width: s.width, height: s.height };
        slots[key] = { width: min.width, height: min.height };
      });
      this.postToFrame({ source: MSG, type: "slots", slots: slots });
    },

    /** An open preview window tracks the editor, rather than freezing at whatever it opened on. */
    postToPreview(payload) {
      if (!this.previewWindow || this.previewWindow.closed || !this.frameOrigin) return;
      this.previewWindow.postMessage(
          payload || { source: MSG, type: "schema", schema: JSON.parse(JSON.stringify(this.schema)) },
          this.frameOrigin);
    },

    onMessage(event) {
      if (!this.frameOrigin || event.origin !== this.frameOrigin) return;
      const data = event.data;
      if (!data || data.source !== MSG) return;

      if (data.type === "ready") {
        // Either the frame or the preview window, and the page says which — `event.source` cannot tell
        // them apart, because a cross-origin navigation gives the opened window a new WindowProxy that
        // never matches the handle `window.open` returned. Both are kept up to date from here on, so a
        // preview left open follows the editor instead of freezing at the draft it opened on.
        if (data.mode === "preview") {
          this.postToPreview();
          return;
        }
        // Mounted and listening. This is what makes the FIRST post land — a post sent before this
        // would have been dropped, which is the whole reason the frame announces itself.
        this.postSlots();
        this.postSchema();
        return;
      }
      if (data.type === "patch") {
        this.applyPatch(data.blockId, data.field, data.value);
        return;
      }
      if (data.type === "select") {
        this.selectedId = data.blockId || "";
        return;
      }
      if (data.type === "image") {
        this.pendingImage = { blockId: data.blockId, field: data.field, index: data.index };
        this.$refs.file.value = "";
        this.$refs.file.click();
        return;
      }
      if (data.type === "move") {
        this.move(data.blockId, data.direction);
        return;
      }
      if (data.type === "remove") {
        const block = this.blocks.find(b => b.id === data.blockId);
        if (block) this.confirmRemove(block);
      }
    },

    /* ── editing ────────────────────────────────────────────────────────────────── */

    applyPatch(blockId, field, value) {
      const block = this.blocks.find(b => b.id === blockId);
      if (!block) return;
      setPath(block, field, value);
      // No postSchema: the frame already shows this — it is where the keystroke came from. Sending it
      // back would re-render the element being typed in and move the caret.
      this.queueSave();
    },

    setSeo(field, value) {
      if (!this.schema.seo) this.schema.seo = {};
      this.schema.seo[field] = value;
      this.queueSave();
    },

    selectBlock(id) {
      this.selectedId = id;
      this.postToFrame({ source: MSG, type: "select", blockId: id });
    },

    canMove(block, direction) {
      const pinned = t => t === "hero" || t === "footer";
      if (pinned(block.type)) return false;
      const i = this.blocks.indexOf(block);
      const target = i + direction;
      if (target < 0 || target >= this.blocks.length) return false;
      return !pinned(this.blocks[target].type);
    },

    move(id, direction) {
      this.schema.blocks = moveBlock(this.blocks, id, direction);
      this.postSchema();
      this.queueSave();
    },

    addBlock() {
      if (!this.typeToAdd) return;
      const block = makeBlock(this.typeToAdd, this.blocks);
      // «Det vi tilbyr» under the hero, everything else before the footer — see insertIndex.
      this.schema.blocks.splice(insertIndex(this.blocks, block.type), 0, block);
      this.typeToAdd = "";
      this.selectedId = block.id;
      this.postSchema();
      this.queueSave();
    },

    async confirmRemove(block) {
      if (isRequired(block.type)) return;
      const ok = await this.$refs.confirmDialogue.show({
        title: "Fjern seksjonen",
        message: "Vil du fjerne «" + blockLabel(block.type) + "» fra hjemmesiden? "
            + "Den forsvinner fra den live nettsiden når du publiserer.",
        okButton: "Fjern",
        cancelButton: "Avbryt"
      });
      if (!ok) return;
      this.schema.blocks = this.blocks.filter(b => b.id !== block.id);
      if (this.selectedId === block.id) this.selectedId = "";
      this.postSchema();
      this.queueSave();
    },

    /* ── hjemmeside på eller av ─────────────────────────────────────────────────── */

    /**
     * The switch shows what is LIVE, so it moves only once the server has said yes. Turning it off asks first:
     * it changes what every guest sees at their address, and a merchant who meant to hide one section must not
     * take the whole front page down by mistake.
     */
    async toggleHome(event) {
      const enabled = event.target.checked;
      event.target.checked = this.homeEnabled;
      if (!enabled) {
        const noOrdering = this.services.pickup !== true;
        const ok = await this.$refs.confirmDialogue.show({
          title: "Slå av hjemmesiden",
          message: "Nettstedet ditt åpner da rett på bestillingssiden. Hjemmesiden blir ikke slettet — du kan "
              + "fortsatt redigere den, og slå den på igjen når du vil."
              + (noOrdering ? " Merk: du tar ikke imot bestillinger på nett ennå." : ""),
          okButton: "Slå av",
          cancelButton: "Avbryt"
        });
        if (!ok) return;
      }
      this.switchingHome = true;
      this.imageError = "";
      SiteHomeService.setHomeEnabled(enabled).then(
          () => { this.homeEnabled = enabled; this.switchingHome = false; },
          () => {
            this.switchingHome = false;
            this.imageError = "Kunne ikke " + (enabled ? "slå på" : "slå av") + " hjemmesiden. Prøv igjen.";
          }
      );
    },

    /* ── «Det vi tilbyr» ────────────────────────────────────────────────────────── */

    serviceOn(key) {
      return this.services[key] === true;
    },

    uspNeeds(key) {
      const service = USP_SERVICES.find(s => s.key === key);
      return service ? service.needs : "tjenesten";
    },

    /**
     * A structural change, unlike a word typed on the page: the frame has not drawn it yet, so it is posted
     * back — that is what makes a hidden service turn grey there at once.
     */
    uspChanged() {
      this.postSchema();
      this.queueSave();
    },

    /**
     * Making a point a service gives it the service's icon, and its words only if it has none of its own
     * yet; making it a promise keeps everything the merchant wrote.
     */
    setUspService(index, key) {
      const item = this.selectedUsp.items[index];
      const service = USP_SERVICES.find(s => s.key === key);
      item.service = service ? service.key : "";
      if (service) {
        item.icon = service.icon;
        if (!item.title || item.title === newUspItem("").title) {
          item.title = service.title;
          item.body = service.body;
        }
      }
      this.uspChanged();
    },

    setUspIcon(index, icon) {
      this.selectedUsp.items[index].icon = icon;
      this.uspChanged();
    },

    moveUspItem(index, direction) {
      const items = this.selectedUsp.items;
      const target = index + direction;
      if (target < 0 || target >= items.length) return;
      const [moved] = items.splice(index, 1);
      items.splice(target, 0, moved);
      this.uspChanged();
    },

    async removeUspItem(index) {
      const item = this.selectedUsp.items[index];
      const ok = await this.$refs.confirmDialogue.show({
        title: "Fjern punktet",
        message: "Vil du fjerne «" + (item.title || "punktet") + "» fra «Det vi tilbyr»?",
        okButton: "Fjern",
        cancelButton: "Avbryt"
      });
      if (!ok) return;
      this.selectedUsp.items.splice(index, 1);
      this.uspChanged();
    },

    addUspItem() {
      if (this.selectedUsp.items.length >= MAX_USP_ITEMS) return;
      this.selectedUsp.items.push(newUspItem(this.uspToAdd));
      this.uspToAdd = "";
      this.uspChanged();
    },

    /* ── images ─────────────────────────────────────────────────────────────────── */

    /**
     * The upload happens here because Backoffice holds the session — the frame can only ask.
     *
     * Compressed to WebP with the same helper the product photos use, so a merchant dropping a 6 MB
     * phone photograph onto their homepage does not make the page they wanted to be fast slow — and
     * CROPPED to the shape its slot renders at. Every slot on the page uses `object-fit: cover`, so
     * the browser was already cropping whatever it was handed; doing it here is the difference
     * between a merchant seeing the result and wondering where the top of their photograph went.
     */
    async onFilePicked(event) {
      const input = event.target;
      const file = input.files && input.files[0];
      const target = this.pendingImage;
      this.pendingImage = null;
      // Cleared whatever happens, or picking the SAME file again fires no change event and the
      // merchant who just chose «Velg et annet» cannot choose the first one after all.
      const done = () => { input.value = ""; };
      if (!file || !target) { done(); return; }

      const block = this.blockOf(target.blockId);
      const slot = SiteImageSlots.forHomeField(block.type, target.field);
      this.imageError = "";

      // Judged before a byte is uploaded — a picture that will be refused should never travel, and a
      // crop should be agreed to before it happens rather than reported afterwards.
      let size;
      try {
        size = await ImageCompressor.readDimensions(file);
      } catch (e) {
        this.imageError = "Kunne ikke lese bildet. Er det en gyldig bildefil?";
        done();
        return;
      }

      const plan = ImageCompressor.plan(size.width, size.height, slot);
      const verdict = SiteImageSlots.inspect(size, slot, plan);

      if (verdict.verdict === "tooSmall") {
        this.imageError = verdict.message;
        done();
        return;
      }
      if (verdict.verdict === "crop") {
        const ok = await this.$refs.confirmDialogue.show({
          title: "Bildet beskjæres",
          message: verdict.message,
          okButton: "Last opp likevel",
          cancelButton: "Velg et annet"
        });
        if (!ok) { done(); return; }
      }

      this.saving = true;
      ImageCompressor.compressImageToSlot(file, slot).then((fitted) => {
        MediaFileService.uploadImageFile(fitted.file).then(
            (response) => {
              const url = response.data;
              if (target.field === "images") {
                if (!Array.isArray(this.blockOf(target.blockId).images)) this.blockOf(target.blockId).images = [];
                this.blockOf(target.blockId).images.splice(target.index, 1, { url: url, alt: "" });
              } else {
                setPath(this.blockOf(target.blockId), target.field, url);
              }
              this.saving = false;
              this.postSchema();
              this.queueSave();
              done();
            },
            () => { this.saving = false; this.imageError = "Kunne ikke laste opp bildet. Prøv igjen."; done(); }
        );
      }).catch(() => { this.saving = false; this.imageError = "Kunne ikke behandle bildet. Prøv igjen."; done(); });
    },

    blockOf(id) {
      return this.blocks.find(b => b.id === id) || {};
    },

    /* ── saving ─────────────────────────────────────────────────────────────────── */

    queueSave() {
      if (this.saveTimer) clearTimeout(this.saveTimer);
      this.saveTimer = setTimeout(this.saveDraft, AUTOSAVE_MS);
    },

    saveDraft() {
      if (!this.schema) return;
      this.saving = true;
      SiteHomeService.save(this.schema).then(
          () => {
            this.saving = false;
            this.savedAt = new Date().toLocaleTimeString("nb-NO", { hour: "2-digit", minute: "2-digit" });
          },
          () => { this.saving = false; }
      );
    },

    async publish() {
      // Any queued autosave is redundant — publish carries the schema itself, which is also what stops
      // the two racing and publishing a version older than what is on screen.
      if (this.saveTimer) { clearTimeout(this.saveTimer); this.saveTimer = null; }

      const first = !this.publishedVersion;
      const ok = await this.$refs.confirmDialogue.show({
        title: first ? "Publiser hjemmesiden" : "Publiser endringene",
        message: first
            ? "Hjemmesiden blir synlig for alle på nett. Du kan endre og publisere på nytt når som helst."
            : "Gjestene ser endringene med én gang. Tidligere versjoner kan hentes tilbake.",
        okButton: "Publiser",
        cancelButton: "Avbryt"
      });
      if (!ok) return;

      this.publishing = true;
      SiteHomeService.publish(this.schema).then(
          (response) => {
            this.publishing = false;
            // A first publish puts the page live (the server starts it switched on); later ones never touch the switch.
            if (!this.publishedVersion) this.homeEnabled = true;
            this.publishedVersion = (response.data && response.data.version) || this.publishedVersion;
            this.loadVersions();
            // Publishing pushed this theme onto every location's ordering page, server-side. The
            // parent remounts the other tab so it cannot save the old one back over it.
            this.$emit('published');
          },
          () => { this.publishing = false; }
      );
    },

    async confirmDiscard() {
      const ok = await this.$refs.confirmDialogue.show({
        title: "Forkast endringer",
        message: "Utkastet ditt slettes og du starter fra den publiserte nettsiden igjen.",
        okButton: "Forkast",
        cancelButton: "Avbryt"
      });
      if (!ok) return;
      // Cancel the queued autosave FIRST. Discarding writes the published page over the draft, but a
      // save queued before that call is still coming — it carries the very schema being thrown away
      // and lands a second later, silently putting it back. The discard appears to work and then
      // undoes itself, which is how a merchant loses confidence in the button. Same race publish
      // already guards against.
      if (this.saveTimer) { clearTimeout(this.saveTimer); this.saveTimer = null; }
      SiteHomeService.discardDraft().then((response) => {
        if (response.data && response.data.schema) {
          this.schema = response.data.schema;
          this.postSchema();
        }
      });
    },

    restore(version) {
      // Same race as discard: an autosave already in flight would write the current draft back over
      // the version just restored.
      if (this.saveTimer) { clearTimeout(this.saveTimer); this.saveTimer = null; }
      SiteHomeService.restore(version).then((response) => {
        if (response.data && response.data.schema) {
          this.schema = response.data.schema;
          this.showVersions = false;
          this.postSchema();
        }
      });
    },

    formatTime(iso) {
      if (!iso) return "";
      const d = new Date(iso);
      return isNaN(d.getTime()) ? iso : d.toLocaleString("nb-NO", {
        day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit"
      });
    }
  }
};
</script>

<style scoped>
.sh-editor { padding: 0 12px 24px; }

.sh-editor__head {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  justify-content: space-between;
  padding: 14px 0;
}

.sh-editor__actions { display: flex; flex-wrap: wrap; gap: 8px; }

.sh-editor__body {
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 16px;
  align-items: start;
}

.sh-editor__rail {
  background: var(--bo-surface);
  border: 1px solid var(--bo-line);
  border-radius: 8px;
  padding: 14px;
  position: sticky;
  /* Same offset as the stage: at plain `12px` this slid under the sticky Backoffice bar. */
  top: calc(var(--bo-nav-offset, 80px) + 12px);
}

.sh-rail__title { font-size: .95rem; margin-bottom: 4px; }
/* backoffice.css floats every switch wrapper left; here it is a line of its own. */
.sh-rail__switch { float: none; clear: both; overflow: auto; margin-bottom: 4px; }
.sh-rail__hint { font-size: .8rem; color: var(--bo-muted); margin-bottom: 10px; }
.sh-rail__list { list-style: none; padding: 0; margin: 0 0 14px; }

.sh-rail__item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 8px;
  border-radius: 6px;
  cursor: pointer;
  border: 1px solid transparent;
}

.sh-rail__item:hover { background: var(--bo-surface-2); }
.sh-rail__item--active { background: var(--bo-brand-tint); border-color: #ECB109; }
.sh-rail__name { flex: 1 1 auto; font-size: .9rem; }
.sh-rail__tools { display: flex; gap: 2px; }

.sh-rail__tools button {
  width: 26px;
  height: 26px;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: var(--bo-muted);
  cursor: pointer;
}

.sh-rail__tools button:hover:not(:disabled) { background: var(--bo-surface-2); color: var(--bo-ink); }
.sh-rail__tools button:disabled { opacity: .25; cursor: not-allowed; }
.sh-rail__remove:hover:not(:disabled) { background: var(--bo-bad-bg) !important; color: var(--bo-bad-fg) !important; }

/* «Det vi tilbyr»: one row per point, ✕ first — the destructive control is never the one a thumb lands on
   reaching for the arrows. */
.sh-usp-panel { border-top: 1px solid var(--bo-line); padding-top: 12px; margin: 4px 0 14px; }
.sh-usp-panel__list { list-style: none; padding: 0; margin: 0 0 10px; display: grid; gap: 8px; }
.sh-usp-panel__item {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 8px;
  border: 1px solid var(--bo-line);
  border-radius: 6px;
}
.sh-usp-panel__remove {
  flex: 0 0 auto;
  width: 26px;
  height: 26px;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: var(--bo-muted);
  cursor: pointer;
}
.sh-usp-panel__remove:hover { background: var(--bo-bad-bg); color: var(--bo-bad-fg); }
.sh-usp-panel__fields { flex: 1 1 auto; min-width: 0; display: grid; gap: 4px; }
.sh-usp-panel__title { font-size: .85rem; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.sh-usp-panel__off { color: var(--bo-muted); font-style: italic; }

.sh-rail__layouts {
  display: grid;
  gap: 6px;
  margin-bottom: 6px;
}

.sh-layout {
  display: block;
  padding: 8px 10px;
  border: 1px solid var(--bo-line);
  border-radius: 6px;
  background: var(--bo-surface);
  cursor: pointer;
  text-align: left;
}

.sh-layout:hover:not(:disabled) { border-color: #ECB109; }
.sh-layout:disabled { opacity: .5; cursor: not-allowed; }

/* In use gets the selected look. Fitting gets only a word, because it is advice, not a state. */
.sh-layout--active { border-color: #ECB109; background: var(--bo-brand-tint); }
.sh-layout__name { display: block; font-size: .85rem; font-weight: 600; }
.sh-layout__desc { display: block; font-size: .76rem; color: var(--bo-muted); line-height: 1.25; }

.sh-layout__tag {
  margin-left: 6px;
  padding: 1px 7px;
  border-radius: 999px;
  border: 1px solid var(--bo-line);
  color: var(--bo-muted);
  font-size: .68rem;
  font-weight: 600;
  white-space: nowrap;
}

.sh-layout__tag--active { border-color: #ECB109; background: #ECB109; color: #fff; }

.sh-rail__templates {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 6px;
  margin-bottom: 6px;
}

.sh-tpl {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 6px 8px;
  border: 1px solid var(--bo-line);
  border-radius: 6px;
  background: var(--bo-surface);
  cursor: pointer;
  text-align: left;
}

.sh-tpl:hover:not(:disabled) { border-color: #ECB109; }
.sh-tpl:disabled { opacity: .5; cursor: not-allowed; }

/* Which one the page is on. Without it a merchant clicking through the five has no way to tell where
   they landed — the preview changed, but the picker looked the same before and after. */
.sh-tpl--active {
  border-color: #ECB109;
  background: var(--bo-brand-tint);
  box-shadow: inset 0 0 0 1px #ECB109;
}

.sh-tpl__swatch {
  width: 22px;
  height: 22px;
  border-radius: 4px;
  border: 1px solid var(--bo-line);
  flex: 0 0 auto;
}

.sh-tpl__name { font-size: .8rem; line-height: 1.15; }

.sh-rail__add { border-top: 1px solid var(--bo-line); padding-top: 12px; }
.sh-rail__seo { border-top: 1px solid var(--bo-line); padding-top: 12px; margin-top: 14px; }

/*
  The stage sticks, like the rail beside it.

  It did not, and the frame was sized `100vh - 220px` from wherever it happened to start — which on a
  857-px window put its bottom 145 px BELOW the fold. The merchant scrolled to see the foot of their
  page and the controls scrolled away with it. Sticking it and sizing it against the viewport gives a
  taller frame AND one that is wholly visible.

  Offset by --bo-nav-offset because the Backoffice bar is itself sticky at the top with z-index 1030;
  a sticky element that ignores it slides underneath and loses its first 80 px.
*/
.sh-editor__stage {
  position: sticky;
  top: calc(var(--bo-nav-offset, 80px) + 12px);
  background: var(--bo-surface);
  border: 1px solid var(--bo-line);
  border-radius: 8px;
  overflow: hidden;
}

.sh-stage__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 12px;
  border-bottom: 1px solid var(--bo-line);
  background: var(--bo-surface-2);
}

.sh-stage__device { display: flex; gap: 4px; }

.sh-stage__device button {
  border: 1px solid var(--bo-line);
  background: var(--bo-surface);
  color: var(--bo-ink);
  border-radius: 6px;
  padding: 4px 10px;
  font-size: .82rem;
  cursor: pointer;
}

.sh-stage__device button.active { background: #ECB109; border-color: #ECB109; color: #fff; }
.sh-stage__saved { font-size: .8rem; color: var(--bo-muted); }

.sh-stage__frame { padding: 12px; background: var(--bo-ground); display: flex; justify-content: center; }

.sh-stage__frame iframe {
  width: 100%;
  /* What is actually left: the sticky bar, this panel's own 12 px gap above, the device toolbar (48),
     the frame's padding (24) and 12 px of air below, so the whole frame lands inside the viewport. */
  height: calc(100vh - var(--bo-nav-offset, 80px) - 96px);
  min-height: 520px;
  border: 0;
  background: var(--bo-surface);
  border-radius: 6px;
  box-shadow: 0 1px 6px rgba(0, 0, 0, .22);
}

/* 390px is a current phone; the point is that the merchant checks it, not that it matches a model. */
.sh-stage__frame--mobile iframe { width: 390px; max-width: 100%; }

/* A short list, not a band across the screen. Bounded and pushed right so it sits under the button
   that opens it, and capped in height because the history holds up to twenty entries — unbounded it
   would shove the editor itself off the screen. */
.sh-editor__actions { position: relative; }

.sh-editor__blocked {
  flex-basis: 100%;
  text-align: right;
  font-size: .8rem;
  color: var(--bo-bad-fg);
}

.sh-versions {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 30;
  width: 480px;
  max-width: calc(100vw - 32px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, .18);
  background: var(--bo-surface);
  border: 1px solid var(--bo-line);
  border-radius: 8px;
  padding: 14px;
}

.sh-versions__title { font-size: .95rem; margin-bottom: 4px; }
.sh-versions__hint { font-size: .8rem; color: var(--bo-muted); margin-bottom: 10px; }

.sh-versions__list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 260px;
  overflow-y: auto;
}

.sh-versions__list li {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 0;
  border-top: 1px solid var(--bo-line);
}

.sh-versions__list li:first-child { border-top: 0; }
.sh-versions__no { font-size: .9rem; white-space: nowrap; }
.sh-versions__when { flex: 1 1 auto; font-size: .8rem; color: var(--bo-muted); }

@media (max-width: 991px) {
  .sh-versions { width: auto; left: 0; right: 0; }
}

@media (max-width: 991px) {
  .sh-editor__body { grid-template-columns: 1fr; }
  .sh-editor__rail { position: static; }
  /* One column: the stage sits below the rail, so sticking it would pin it over the controls. */
  .sh-editor__stage { position: static; }
  .sh-stage__frame iframe { height: 70vh; }
}
</style>
